/**
 * The one DOM question the window-level handlers keep having to ask.
 *
 * Duck-typed rather than instance-checked, so it needs no DOM to test — the suite has no jsdom, and
 * a guard that decides whether a keystroke reaches your text is not something to leave untested
 * because it was awkward to reach.
 */

/**
 * True when the event landed in something you type into.
 *
 * Five callers, and they need it for different reasons:
 *
 * - **Cards' J/K keys.** `J` in the dock's English field does not merely swallow a keystroke — it
 *   moves `focused`, which remounts the dock on another note and drops the unsaved draft.
 * - **Cards' paste-to-attach.** A browser copy carries `text/html` *and* an image (the case
 *   `firstImage` exists for), so an unguarded paste handler that `preventDefault()`s would swallow
 *   a legitimate text paste into a field and put its illustration on the card instead.
 * - **Read's shortcuts.** `j`, `k` and `Tab` would otherwise never reach the field you are typing
 *   into.
 * - **Read's two paste-to-attach handlers**, `AddedPanel`'s and the sentence-slot one, which are
 *   the same trap as Cards' and were missed when it was closed there. The panel's is the worse of
 *   the two: it is open precisely when you have just carded a word, and its English field is
 *   exactly where you would paste a definition.
 *
 * TEXTAREA as well as INPUT: the English field is a textarea. `tagName` is compared uppercase,
 * which is what the DOM reports for HTML elements.
 */
export function isTextEntry(target: EventTarget | null | undefined): boolean {
  const element = target as { tagName?: string; isContentEditable?: boolean } | null | undefined;
  if (!element) return false;
  return (
    element.isContentEditable === true ||
    element.tagName === "INPUT" ||
    element.tagName === "TEXTAREA"
  );
}
